/**
 * Основные сущности приложения
 */
import {
  type BoxId,
  type EntityTypeId,
  type JournalId,
  type ProductId,
  type UserId,
} from "./ids";
import { type IDate } from "./date";

/**
 * Типы измерения для продуктов
 */
export enum Measures {
  /** Литры */
  liters = "liters",
  /** Вес */
  weight = "weight",
  /** Количество */
  quantity = "quantity",
}

/**
 * Типы действий с продуктами
 */
export enum Action {
  /** Взять продукт */
  take = "take",
  /** Положить продукт */
  put = "put",
  /** Утилизировать продукт */
  destroy = "destroy",
}

/**
 * Интерфейс продукта
 */
export interface IProduct {
  /** Уникальный идентификатор продукта */
  id: ProductId;
  /** Название продукта */
  name: string;
  /** URL фото продукта */
  photo: string;
  /** Дополнительные URL фото продукта (первое фото синхронизировано с `photo`) */
  photos?: string[];
  /** Описание продукта */
  description: string;
  /** Дата производства */
  manufacturingDate: IDate | null;
  /** Срок годности */
  expiryDate: IDate | null;
  /** Теги типа продукта (могут быть множественными) */
  typeTags?: string[];
  /** Единица измерения */
  measure: Measures;
  /** Количество в выбранной единице измерения */
  quantity: number;
  /** Идентификатор коробки, в которой находится продукт */
  parentId: BoxId | null;
}

/**
 * Интерфейс типа сущности
 */
export interface IEntityType {
  /** Уникальный идентификатор типа */
  id: EntityTypeId;
  /** Название типа */
  name: string;
  /** Дополнительное описание типа (опционально) */
  description?: string;
  /** Дополнительные свойства типа (опционально) */
  properties?: Record<string, unknown>;
}

/**
 * Интерфейс коробки
 */
export interface IBox {
  /** Уникальный идентификатор коробки */
  id: BoxId;
  /** Название коробки */
  name: string;
  /** URL фото коробки */
  photo: string;
  /** Теги типа коробки (могут быть множественными) */
  typeTags?: string[];
  /** Описание коробки */
  description: string;
  /** Идентификатор родительской коробки */
  parentId: BoxId | null;
  /** Метаданные коробки (опционально) */
  metadata?: Record<string, unknown>;
  /** Размеры коробки в см (опционально) */
  dimensions?: {
    width: number;
    height: number;
    depth: number;
  };
}

/**
 * Интерфейс пользователя
 */
export interface IUser {
  /** Уникальный идентификатор пользователя */
  id: UserId;
  /** Имя пользователя */
  name: string;
  /** URL фото пользователя */
  photo: string;
  /** Пароль или ключ доступа */
  pasKey: string;
  /** Роль пользователя (опционально) */
  role?: "admin" | "user" | "viewer";
  /** Статус пользователя (опционально) */
  status?: "pending" | "active";
  /** Дата последней активности (опционально) */
  lastActive?: IDate;
  /** Настройки пользователя (опционально) */
  settings?: Record<string, unknown>;
}

/**
 * Интерфейс журнала операций
 */
export interface IJournal {
  /** Уникальный идентификатор записи (опционально) */
  id?: JournalId;
  /** Дата и время операции */
  date: IDate;
  /** Пользователь, выполнивший операцию */
  user: IUser;
  /** Тип действия */
  action: Action;
  /** Идентификатор продукта */
  productId: ProductId;
  /** Идентификатор коробки, откуда/куда был перемещен продукт */
  fromTo: BoxId;
  /** Количество продукта */
  value: number;
  /** Комментарий к операции (опционально) */
  comment?: string;
  /** Метаданные операции (опционально) */
  metadata?: Record<string, unknown>;
}
